import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useFeature } from '../contexts/FeatureFlagsContext';
import './SubscriptionGate.css';
import { isAdminRole } from '../utils/planAccess';

/**
 * FeatureGate - Blocks access based on feature flags AND subscription status
 *
 * Priority order:
 * 1. Admin users always have access
 * 2. Check feature flag (global toggle, plan restrictions, user overrides)
 * 3. Check subscription status (trial, paid plan)
 *
 * @param {string} featureKey - The feature flag key to check (e.g., 'ai_clips', 'live_streaming')
 * @param {string} feature - Human-readable feature name for display
 * @param {ReactNode} children - Content to show if access is granted
 */
const FeatureGate = ({ featureKey, feature = 'this feature', children }) => {
  const { currentUser, getSubscriptionStatus } = useAuth();
  const { hasAccess: featureEnabled, reason: featureReason, loading } = useFeature(featureKey);
  const navigate = useNavigate();

  // Show loading state while checking feature flags
  if (loading) {
    return (
      <div className="subscription-gate">
        <div className="subscription-gate-loading">
          <div className="loading-spinner"></div>
          <p>Checking access...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return children; // Let PrivateRoute handle auth
  }

  // Admins always have access
  if (isAdminRole(currentUser.role)) {
    return children;
  }

  const status = getSubscriptionStatus();

  // Check feature flag first (if featureKey provided)
  if (featureKey && !featureEnabled) {
    return (
      <div className="subscription-gate">
        <div className="subscription-gate-card">
          <div className="subscription-gate-icon feature-locked">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="64" height="64">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2>Feature Not Available</h2>
          <p>{getFeatureMessage(featureReason, feature)}</p>
          {renderUpgradePrompt(featureReason, navigate)}
        </div>
      </div>
    );
  }

  // Check subscription status
  if (!status.hasAccess) {
    return (
      <div className="subscription-gate">
        <div className="subscription-gate-card">
          <div className="subscription-gate-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="64" height="64">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2>{status.trialExpired ? 'Your free trial has ended' : 'Upgrade to continue'}</h2>
          <p>
            {status.trialExpired
              ? `${feature} needs an active plan. Upgrade to pick up right where you left off — founding rate, locked for life.`
              : `${feature} is available on a paid plan. Start your 14-day free trial — cancel anytime.`
            }
          </p>
          <div className="subscription-gate-features">
            <h3>What you get:</h3>
            <ul>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <strong>14-day free trial</strong> — no charge for 14 days
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <strong>Founding rate</strong> — locked for life, even after public launch
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <strong>Cancel anytime</strong> — manage it yourself from your account
              </li>
            </ul>
          </div>
          <div className="subscription-gate-actions">
            <button className="gate-btn-primary" onClick={() => navigate('/pricing')}>
              View plans
            </button>
            <button className="gate-btn-secondary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Access granted
  return children;
};

/**
 * Get user-friendly message based on restriction reason
 */
function getFeatureMessage(reason, feature) {
  if (!reason) {
    return `Access to ${feature} is currently unavailable.`;
  }

  if (reason.includes('disabled')) {
    return `${feature} is temporarily disabled for maintenance. Please check back soon.`;
  }

  if (reason.includes('beta')) {
    return `${feature} is currently in beta testing. Join our beta program to get early access.`;
  }

  if (reason.includes('plan') || reason.includes('Requires')) {
    return `${feature} is available on a paid plan. Upgrade to unlock it.`;
  }

  if (reason.includes('blocked')) {
    return `Your access to ${feature} has been restricted. Please contact support if you believe this is an error.`;
  }

  if (reason.includes('not yet available')) {
    return `${feature} is being rolled out gradually and will be available to your account soon.`;
  }

  return `Access to ${feature} is not available. ${reason}`;
}

/**
 * Render appropriate upgrade/action buttons based on restriction type
 */
function renderUpgradePrompt(reason, navigate) {
  if (reason?.includes('beta')) {
    return (
      <div className="subscription-gate-actions">
        <button className="gate-btn-primary" onClick={() => window.open('mailto:beta@canira.io', '_blank')}>
          Request Beta Access
        </button>
        <button className="gate-btn-secondary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (reason?.includes('blocked')) {
    return (
      <div className="subscription-gate-actions">
        <button className="gate-btn-primary" onClick={() => navigate('/admin/support')}>
          Contact Builder Success
        </button>
        <button className="gate-btn-secondary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (reason?.includes('disabled')) {
    return (
      <div className="subscription-gate-actions">
        <button className="gate-btn-secondary" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  // Default: plan upgrade needed → self-serve /pricing page.
  return (
    <div className="subscription-gate-actions">
      <button className="gate-btn-primary" onClick={() => navigate('/pricing')}>
        View plans
      </button>
      <button className="gate-btn-secondary" onClick={() => navigate('/dashboard')}>
        Back to Dashboard
      </button>
    </div>
  );
}

export default FeatureGate;
